/* ============================================================
   OS9 BROWSER WINDOW — Chrome + dynamic tab routing
   squiche.com

   Renders the Internet Browser window: menubar, tabs, IE-style
   toolbar, address bar, bookmarks, dialup animation, and page
   routing. Page content is provided by OS9BrowserPages modules.

   DRAG HANDLE NOTE (2026-05-20 — reverted)
   ──────────────────────────────────────────
   blockDragIfMaximized now attaches to [data-drag] (the titlebar).
   Drag moved back to the titlebar — bottom bar is resize-only.
   See initDrag in os9-window-manager.js for full history.

   DIALUP OVERLAY
   ──────────────
   Always attaches to .os9-browser-content, not to individual
   page containers, so positioning is consistent on every page.

   ── ROADMAP 2026-05-18 ──────────────────────────────────────
   P0 — DONE: blockDragIfMaximized reverted to target [data-drag]
        (titlebar) after drag moved back off the bottom bar (2026-05-20)
   P1 — TODO: Tab close button restores only if no other flash
        game is using the sidebar — currently always restores.
   P2 — TODO: Favorites dropdown (bmDropdown) is appended to
        document.body and never cleaned up if the window is
        closed while the dropdown is open. Low-priority leak.
   ─────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var _winEl       = null;

  /* ═══════════════════════════════════════════════════════════
     HOME_PAGE — which registered page is this browser's home.

     2026-09-28: SquichaVista, not the GeoCities page. The search
     engine is the hub every lander is reached through, so New Tab
     lands on a fresh search the way a period browser did, and the
     GeoCities page became an ordinary lander ('geocities') reachable
     as a SquichaVista result like any other brand.

     'home' used to mean two things at once — the role (first tab,
     Home button target) and the GeoCities page key. Separating them
     is what lets the home page be swapped by editing this one line.
     ═══════════════════════════════════════════════════════════ */
  var HOME_PAGE    = 'altavista';
  var HOME_URL     = 'squiche.com';

  var _currentPage = HOME_PAGE;

  /* ── CDN assets ── */
  var IE_LOGO_SRC = 'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/SQUICHEEXPLORER.png?v=1779036030';
  var DIALUP_GIF  = 'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/internet-dial-up.gif?v=1779025460';

  var IE_ICONS = {
    Back:      'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Back.png?v=1779025030',
    Forward:   'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Forward.png?v=1779025030',
    Stop:      'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Stop.png?v=1779025030',
    Refresh:   'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Refresh.png?v=1779025031',
    Home:      'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Home.png?v=1779025030',
    Search:    'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Search.png?v=1779025030',
    Favorites: 'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Favorites.png?v=1779025030',
    Print:     'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Print.png?v=1779025030',
    Fonts:     'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Fonts.png?v=1779025030',
    Mail:      'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Mail_a66da036-ddd0-4cf4-a9ec-ac905560601d.png?v=1779025030',
    Edit:      'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/Edit.png?v=1779025030'
  };

  /* Links out to the real web, listed under Favorites and opened in a new tab
     rather than inside the OS9 browser.

     Deliberately not embedded. These are other people's sites: we do not
     control whether they keep allowing framing or stay up, a broken iframe
     would read as our bug, and a heavy WASM build inside the desktop would
     compete for bandwidth with the product images on the page someone is
     meant to be shopping. A 1999 browser keeping bookmarks to places on the
     real web is also just the more honest gesture.

     Hardcoded rather than a theme-editor setting because the Explorer schema
     is a no-touch zone without Dalton's say-so. Easy to promote to a schema
     setting later if the list starts changing often. */
  var EXTERNAL_FAVORITES = [
    { icon: '🏎️', label: 'Step On It',
      url: 'https://steponit.online/' },
  ];

  function _toolBtn(id, iconKey, label, disabled, desktopOnly) {
    var cls = 'os9-browser__tool-btn' +
      (disabled    ? ' os9-browser__tool-btn--disabled' : '') +
      (desktopOnly ? ' os9-browser__tool-btn--desktop'  : '');
    return '<button class="' + cls + '" id="' + id + '">' +
      '<span class="os9-browser__tool-icon"><img src="' + (IE_ICONS[iconKey] || '') + '" alt="' + label + '"></span>' +
      '<span class="os9-browser__tool-label">' + label + '</span>' +
    '</button>';
  }

  /* ── Chrome CSS ── */
  var chromeCSS = [
    /* base */
    '.os9-browser{display:flex;flex-direction:column;width:100%;height:100%;min-width:0;min-height:0;background:var(--os9-gray-300);font-family:var(--os9-font-body);font-size:var(--os9-size-title);overflow:hidden;box-sizing:border-box;}',
    /* FIX: Zero out any padding the window manager puts on [data-body].
       The grey gap was caused by inherited padding from the OS9 window chrome. */
    '#os9-browser-win{min-width:220px;}',
    '#os9-browser-win [data-body]{display:flex;flex-direction:column;padding:0 !important;margin:0 !important;overflow:hidden;}',

    /* menu bar — z-index must beat the dialup overlay (z-index:500) so the
       File menu dropdown can render above an in-progress page load. The
       dropdown's own z-index of 1000 only counts INSIDE the menubar's
       stacking context; the menubar itself needs to be above 500 first. */
    '.os9-browser__menubar{display:flex;align-items:center;padding:0 2px;background:var(--os9-gray-300);border-bottom:1px solid var(--os9-gray-500);height:18px;flex-shrink:0;position:relative;z-index:600;}',
    '.os9-browser__menu-item{padding:1px 7px 2px;font-size:var(--os9-size-ui);font-family:var(--os9-font-ui);cursor:default;color:var(--os9-black);white-space:nowrap;list-style:none;}',
    '.os9-browser__menu-item::-webkit-details-marker{display:none;}',
    '.os9-browser__menu-item:hover{background:var(--os9-select);color:var(--os9-white);}',
    '.os9-browser__menu-item.is-disabled{color:var(--os9-gray-600);pointer-events:none;}',
    /* dropdown panel — Mac OS classic look, anchored under the File summary */
    '.os9-browser__menu-section{position:relative;}',
    '.os9-browser__menu-section[open] > summary{background:var(--os9-select);color:var(--os9-white);}',
    /* When the menus sit in the desktop menu bar they inherit its type scale
       and the dropdown hangs from its taller bar. */
    '.os9-menubar__group{display:flex;align-items:center;}',
    '#os9-mb-app .os9-browser__menu-item{font-size:var(--os9-size-title);font-family:var(--os9-font-ui);padding:3px 9px;}',
    '#os9-mb-app .os9-browser__menu-dropdown{top:21px;}',
    '.os9-browser__menu-dropdown{position:absolute;top:18px;left:0;min-width:240px;background:var(--os9-gray-300);border:1px solid var(--os9-black);box-shadow:2px 2px 4px rgba(0,0,0,0.4);z-index:1000;padding:3px 0;}',
    '.os9-browser__menu-dd-item{display:flex;align-items:center;justify-content:space-between;padding:3px 16px 3px 20px;font-size:var(--os9-size-ui);font-family:var(--os9-font-ui);color:var(--os9-black);text-decoration:none;cursor:default;white-space:nowrap;gap:24px;}',
    '.os9-browser__menu-dd-item:hover{background:var(--os9-select);color:var(--os9-white);}',
    '.os9-browser__menu-dd-item.is-disabled{color:var(--os9-gray-600);pointer-events:none;}',
    '.os9-browser__menu-dd-item.is-disabled:hover{background:transparent;color:var(--os9-gray-600);}',
    '.os9-browser__menu-dd-sep{border-top:1px solid var(--os9-gray-600);border-bottom:1px solid var(--os9-white);margin:3px 0;}',
    '.os9-browser__menu-shortcut{font-family:var(--os9-font-body);font-size:var(--os9-size-body);opacity:0.8;}',

    /* tabs */
    '.os9-browser__tabs{display:flex;align-items:flex-end;padding:4px 4px 0;background:var(--os9-gray-400);border-bottom:1px solid var(--os9-gray-600);gap:2px;overflow-x:auto;flex-shrink:0;}',
    '.os9-browser__tab{display:flex;align-items:center;gap:4px;padding:3px 10px 4px;background:var(--os9-gray-300);border:1px solid var(--os9-gray-600);border-bottom:none;font-size:var(--os9-size-ui);font-family:var(--os9-font-ui);border-radius:3px 3px 0 0;cursor:default;white-space:nowrap;flex-shrink:0;}',
    '.os9-browser__tab--active{background:var(--os9-white);font-weight:bold;}',
    '.os9-browser__tab-add{padding:3px 8px 4px;background:var(--os9-gray-400);border:1px solid var(--os9-gray-600);border-bottom:none;border-radius:3px 3px 0 0;cursor:default;font-size:14px;line-height:1;flex-shrink:0;}',
    '.os9-browser__tab-add:hover{background:var(--os9-gray-500);}',
    '.os9-browser__tab-close{margin-left:6px;font-size:var(--os9-size-title);line-height:1;opacity:0.45;padding:0 3px;border-radius:2px;}',
    '.os9-browser__tab-close:hover{opacity:1;background:rgba(0,0,0,0.15);}',

    /* FIX: toolbar must NOT use overflow:hidden — that was preventing the spacer
       from pushing the logo to the right. Use overflow:visible + clip via the
       outer wrapper instead. Logo alignment fix: use margin-left:auto on spacer. */
    '.os9-browser__toolbar{display:flex;align-items:stretch;padding:2px 0 2px 2px;background:var(--os9-gray-300);border-bottom:1px solid var(--os9-gray-500);gap:0;min-height:44px;flex-shrink:0;}',
    '.os9-browser__tool-btn{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;padding:2px 4px 3px;background:transparent;border:1px solid transparent;cursor:default;min-width:44px;flex-shrink:0;font-family:inherit;}',
    '.os9-browser__tool-btn:hover{background:var(--os9-gray-400);border-color:var(--os9-white) var(--os9-gray-600) var(--os9-gray-600) var(--os9-white);}',
    '.os9-browser__tool-btn:active,.os9-browser__tool-btn--active{background:var(--os9-gray-500);border-color:var(--os9-gray-600) var(--os9-white) var(--os9-white) var(--os9-gray-600);}',
    '.os9-browser__tool-btn--disabled{opacity:0.45;pointer-events:none;}',
    '.os9-browser__tool-icon{width:24px;height:24px;display:flex;align-items:center;justify-content:center;}',
    '.os9-browser__tool-icon img{max-width:100%;max-height:100%;display:block;image-rendering:-webkit-optimize-contrast;image-rendering:crisp-edges;}',
    '.os9-browser__tool-label{font-size:var(--os9-size-tiny);color:var(--os9-black);font-family:var(--os9-font-body);white-space:nowrap;line-height:1;}',
    '.os9-browser__toolbar-sep{width:1px;background:var(--os9-gray-600);box-shadow:1px 0 0 var(--os9-white);margin:4px 3px;flex-shrink:0;}',
    '.os9-browser__toolbar-sep--desktop{display:block;}',
    /* Spacer is kept for any visual gap but margin-left:auto on the logo is what
       actually clamps it right — evaluated before flex space distribution */
    '.os9-browser__toolbar-spacer{flex:1;min-width:0;}',

    /* IE logo: margin-left:auto is the definitive right-lock mechanism.
       flex-shrink:0 + explicit width prevent any compression. */
    '.os9-browser__ie-logo{margin-left:auto;width:44px;min-width:44px;max-width:44px;flex-shrink:0;flex-grow:0;background:transparent;border-left:1px solid var(--os9-gray-500);display:flex;align-items:center;justify-content:center;overflow:hidden;align-self:stretch;}',
    '.os9-browser__ie-logo img{width:36px;height:36px;display:block;image-rendering:-webkit-optimize-contrast;image-rendering:crisp-edges;}',

    /* address bar */
    '.os9-browser__addressbar{display:flex;align-items:center;padding:2px 3px;background:var(--os9-gray-300);border-bottom:1px solid var(--os9-gray-500);gap:3px;height:24px;flex-shrink:0;}',
    '.os9-browser__address-label{font-size:var(--os9-size-ui);font-family:var(--os9-font-ui);flex-shrink:0;padding-right:3px;border-right:1px solid var(--os9-gray-500);margin-right:2px;}',
    '.os9-browser__address-input-wrap{flex:1;min-width:0;height:18px;background:var(--os9-white);border:2px solid;border-color:var(--os9-gray-600) var(--os9-gray-200) var(--os9-gray-200) var(--os9-gray-600);display:flex;align-items:center;overflow:hidden;}',
    '.os9-browser__address-input{flex:1;min-width:0;border:none;outline:none;background:transparent;height:100%;padding:0 3px;font-size:var(--os9-size-ui);font-family:var(--os9-font-body);cursor:text;}',
    '.os9-browser__address-dropdown{width:16px;height:100%;background:var(--os9-gray-300);border-left:1px solid var(--os9-gray-600);display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:8px;cursor:default;}',
    '.os9-browser__address-dropdown:hover{background:var(--os9-gray-400);}',
    '.os9-browser__links-btn{height:18px;padding:0 6px;background:var(--os9-gray-300);border:2px solid;border-color:var(--os9-white) var(--os9-gray-600) var(--os9-gray-600) var(--os9-white);font-size:var(--os9-size-ui);font-family:var(--os9-font-body);cursor:default;white-space:nowrap;display:flex;align-items:center;flex-shrink:0;}',
    '.os9-browser__links-btn:hover{background:var(--os9-gray-400);}',

    /* bookmarks */
    '.os9-browser__bookmarks{display:flex;align-items:center;padding:2px 6px;background:var(--os9-gray-300);border-bottom:1px solid var(--os9-gray-500);gap:0;overflow-x:auto;flex-shrink:0;}',
    '.os9-browser__bookmark{padding:2px 8px;font-size:var(--os9-size-ui);color:var(--os9-link);text-decoration:underline;cursor:default;white-space:nowrap;}',
    '.os9-browser__bookmark:hover{background:var(--os9-gray-400);}',

    /* status */
    /* Hidden at rest so the browser's bottom edge matches the other windows,
       and shown only while a page is "loading" — which is what the status
       line was always for. It stops where the vertical channel begins so that
       channel stays unbroken. */
    '.os9-browser__status{display:none;height:18px;align-items:center;padding:0 6px;margin-right:var(--os9-scroll-w);background:var(--os9-gray-300);border-top:1px solid var(--os9-gray-600);font-size:var(--os9-size-body);color:var(--os9-gray-800);font-family:var(--os9-font-body);flex-shrink:0;}',
    '.os9-browser__status.is-loading{display:flex;}',

    /* bookmarks dropdown */
    '#os9-bm-dropdown{position:fixed;background:linear-gradient(to bottom,var(--os9-gray-200),var(--os9-gray-300));border:1px solid var(--os9-gray-600);box-shadow:3px 3px 6px rgba(0,0,0,0.4);z-index:99999;min-width:180px;padding:2px 0;}',
    '#os9-bm-dropdown a{display:flex;align-items:center;height:22px;padding:0 16px;font-size:var(--os9-size-ui);font-family:var(--os9-font-body);color:var(--os9-black);text-decoration:none;cursor:default;white-space:nowrap;}',
    '#os9-bm-dropdown a:hover{background:var(--os9-select);color:var(--os9-white);}',
    '#os9-bm-dropdown hr{border:none;border-top:1px solid var(--os9-gray-500);margin:2px 0;}',

    /* layout */
    /* Right rule. The vertical channel draws its own right edge, but it only
       begins at the top of the content area — above that the toolbar block
       runs to the window edge with nothing bounding it, leaving the window's
       right side broken between the title bar and the scroll bar. This
       carries the line down across the chrome so the two meet. Greyed with
       everything else when the window is in the background. */
    /* Print: everything but the cloned page is suppressed. */
    '#os9-print-root{display:none;}',
    '@media print{html.os9-printing body>*:not(#os9-print-root){display:none !important;}',
    'html.os9-printing #os9-print-root{display:block !important;position:static !important;background:#fff;}',
    'html.os9-printing #os9-print-root img{max-width:100% !important;height:auto !important;}}',
    '.os9-browser-chrome{flex-shrink:0;background:var(--os9-gray-300);border-right:1px solid var(--os9-black);}',
    '.os9-win.is-background .os9-browser-chrome{border-right-color:var(--os9-rule-off);}',

    /* FIX: content area is the single fixed viewport. Dialup overlays attach HERE
       not to individual page elements, so position is always consistent. */
    '.os9-browser-content{flex:1;overflow:clip;min-height:0;background:var(--os9-white);position:relative;}',

    /* page containers fill the content area fully */
    /* BOTH axes on this one element. The lanes only form an L with a size box
       in the corner if they belong to the same scroller — with vertical here
       and horizontal on the shell they were in different boxes and never met,
       which is what made the browser's bottom edge read differently from
       every other window. `scroll` not `auto`, matching .os9-win__scroll, so
       the channels are always drawn and the corner always exists. */
    '[id^="os9-page-"]{position:absolute;inset:0;overflow:scroll;background:var(--os9-white);-webkit-overflow-scrolling:touch;}',
    '[id^="os9-page-"][style*="display:none"]{display:none !important;}',

    /* FIX: dial-up attaches to .os9-browser-content (the fixed viewport),
       not to individual page divs. This guarantees consistent centering. */
    '.os9-dialup{position:absolute;inset:0;background:var(--os9-white);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;z-index:500;}',
    '.os9-dialup--gone{display:none !important;}',
    '.os9-dialup__gif{width:auto;height:80px;display:block;}',
    '.os9-dialup__title{font-size:var(--os9-size-title);font-family:var(--os9-font-ui);color:var(--os9-black);text-align:center;}',
    '.os9-dialup__host{font-size:var(--os9-size-body);font-family:var(--os9-font-body);color:var(--os9-gray-800);max-width:300px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-align:center;}',
    '.os9-dialup__loader{width:auto;height:48px;display:block;}',

    /* mobile: hide desktop-only buttons below 480px */
    '@media(max-width:480px){',
      '.os9-browser__tool-btn--desktop{display:none !important;}',
      '.os9-browser__toolbar-sep--desktop{display:none !important;}',
      '.os9-browser__tool-btn{min-width:36px;padding:2px 2px 3px;}',
      '.os9-browser__ie-logo{width:36px;min-width:36px;max-width:36px;}',
      '.os9-browser__ie-logo img{width:28px;height:28px;}',
    '}'
  ].join('');

  function injectCSS() {
    /* Replace existing sheet so hot-reloads work */
    var prev = document.getElementById('os9-browser-chrome-css');
    if (prev) prev.parentNode.removeChild(prev);
    var s = document.createElement('style');
    s.id = 'os9-browser-chrome-css';
    s.textContent = chromeCSS;
    document.head.appendChild(s);
  }

  function getPageConfig(type) {
    var pages = (window.OS9Config && window.OS9Config.browserPages) || [];
    for (var i = 0; i < pages.length; i++) {
      if (pages[i].type === type) return pages[i];
    }
    return null;
  }

  /* ─────────────────────────────────────────────────────────────
     showDialup(url)
     FIX: Always attaches to .os9-browser-content, not to page
     containers — gives identical centering on every page.
     ───────────────────────────────────────────────────────────── */
  function showDialup(url) {
    if (!_winEl) return Promise.resolve();

    /* Always attach to the content viewport */
    var contentEl = _winEl.querySelector('.os9-browser-content');
    if (!contentEl) return Promise.resolve();

    /* Remove any existing overlay */
    var old = contentEl.querySelector('.os9-dialup');
    if (old) old.parentNode.removeChild(old);

    return new Promise(function (resolve) {
      var totalMs    = 2200 + Math.random() * 1300;
      var displayUrl = url || 'squiche.com';
      var overlay = document.createElement('div');
      overlay.className = 'os9-dialup';
      overlay.innerHTML =
        '<img class="os9-dialup__gif" src="' + DIALUP_GIF + '" alt="Connecting...">' +
        '<div class="os9-dialup__title">Opening page\u2026</div>' +
        '<div class="os9-dialup__host">' + displayUrl + '</div>' +
        '<img class="os9-dialup__loader" src="https://cdn.shopify.com/s/files/1/0777/2844/8754/files/lader_giphy.gif?v=1779995013" alt="Loading...">';

      contentEl.appendChild(overlay);

      /* Spend the fake delay on real work.

         switchToPage() has already rendered the destination behind this
         overlay, and its images are lazy — so they would otherwise start
         loading only once the modem noise finishes and the page is revealed,
         which is the exact moment the visitor is looking at it. Warming them
         now means the ~2.5s of theatre is also 2.5s of real fetching, and the
         page is complete when it appears instead of assembling itself.

         This is the whole reason the dial-up delay is worth keeping: it buys
         genuine load time rather than only pretending to. */
      try {
        var pageEl = _winEl.querySelector('#os9-page-' + _currentPage);
        var warm = window.OS9Windows && window.OS9Windows.warmImages;
        if (pageEl && warm) {
          warm([].slice.call(pageEl.querySelectorAll('img'))
                 .map(function (i) { return i.getAttribute('src') || i.getAttribute('data-src'); })
                 .filter(Boolean));
        }
      } catch (e) { /* warming is best-effort; never block the page on it */ }


      /* Status bar cycling */
      var statusEl = _winEl.querySelector('#os9-browser-status');

      /* One joke, dealt at random into the middle of the real chatter — rare
         enough to be worth catching, and it reads as a genuine status line
         rather than a gag reel. Add to ASIDES to add more. */
      var ASIDES = [
        'Bribing the modem\u2026',
        'Asking your mom to get off the phone\u2026',
        'Rendering at 56k, please enjoy the wait\u2026',
        'Untangling the phone cord\u2026',
        'Consulting the manual\u2026',
        'Blowing into the cartridge\u2026',
        'Reticulating splines\u2026',
        'Downloading more RAM\u2026'
      ];
      var msgs = [
        'Opening page ' + displayUrl,
        'Connecting to site\u2026',
        'Host contacted. Waiting for reply\u2026',
        'Transferring data from ' + displayUrl + '\u2026',
        'Receiving data\u2026',
        'Loading images\u2026',
        'Done'
      ];
      msgs.splice(2 + Math.floor(Math.random() * 3), 0,
                  ASIDES[Math.floor(Math.random() * ASIDES.length)]);
      if (statusEl) statusEl.classList.add('is-loading');
      var mIdx = 0;
      var msgInterval = setInterval(function () {
        if (statusEl && mIdx < msgs.length) statusEl.textContent = msgs[mIdx++];
      }, totalMs / msgs.length);

      setTimeout(function () {
        clearInterval(msgInterval);
        if (statusEl) statusEl.textContent = 'Done';
        setTimeout(function () {
          overlay.classList.add('os9-dialup--gone');
          /* Back to hidden — the footer exists for the load, not after it. */
          if (statusEl) statusEl.classList.remove('is-loading');
          resolve();
        }, 180);
      }, totalMs);
    });
  }

  /* ── Menus ──────────────────────────────────────────────────────────
     In Mac OS the menu bar belongs to the active application, not to its
     window. Squiche Explorer's menus therefore live in the desktop menu bar
     whenever the browser is frontmost, and the window itself carries no menu
     row at all — which is also 19px of chrome back. */
  function browserMenusHTML() {
    return '<details class="os9-browser__menu-section" data-menu="file">' +
            '<summary class="os9-browser__menu-item">File</summary>' +
            '<div class="os9-browser__menu-dropdown">' +
              '<a class="os9-browser__menu-dd-item" data-file-action="new-tab"><span>New Tab</span><span class="os9-browser__menu-shortcut">\u2318T</span></a>' +
              '<a class="os9-browser__menu-dd-item is-disabled"><span>New Window</span><span class="os9-browser__menu-shortcut">\u2318N</span></a>' +
              '<a class="os9-browser__menu-dd-item is-disabled"><span>Open Location\u2026</span><span class="os9-browser__menu-shortcut">\u2318L</span></a>' +
              '<div class="os9-browser__menu-dd-sep"></div>' +
              '<a class="os9-browser__menu-dd-item" data-file-action="close-window"><span>Close Window</span><span class="os9-browser__menu-shortcut">\u21E7\u2318W</span></a>' +
              '<a class="os9-browser__menu-dd-item" data-file-action="close-tab"><span>Close Tab</span><span class="os9-browser__menu-shortcut">\u2318W</span></a>' +
              '<a class="os9-browser__menu-dd-item is-disabled"><span>Save Page As\u2026</span><span class="os9-browser__menu-shortcut">\u2318S</span></a>' +
              '<a class="os9-browser__menu-dd-item" data-file-action="share"><span>Share</span></a>' +
              '<a class="os9-browser__menu-dd-item" data-file-action="print"><span>Print\u2026</span><span class="os9-browser__menu-shortcut">\u2318P</span></a>' +
            '</div>' +
          '</details>' +
          '<span class="os9-browser__menu-item is-disabled">Edit</span>' +
          '<span class="os9-browser__menu-item is-disabled">View</span>' +
          '<span class="os9-browser__menu-item is-disabled">Go</span>' +
          '<span class="os9-browser__menu-item is-disabled">Favorites</span>' +
          '<span class="os9-browser__menu-item is-disabled">Help</span>';
  }


  /* The Finder's own menus are wrapped once, so the two sets can be swapped
     without re-rendering either. */
  function menubarGroups() {
    var bar = document.getElementById('os9-menubar');
    if (!bar) return null;
    var finder = bar.querySelector('#os9-mb-finder');
    if (finder) return { bar: bar, finder: finder, app: bar.querySelector('#os9-mb-app') };

    var right = bar.querySelector('.os9-menubar__right');
    finder = document.createElement('div');
    finder.id = 'os9-mb-finder';
    finder.className = 'os9-menubar__group';
    var move = [];
    Array.prototype.forEach.call(bar.children, function (c) {
      if (c === right) return;
      if (c.classList && c.classList.contains('os9-menubar__apple')) return;
      move.push(c);
    });
    bar.insertBefore(finder, right);
    move.forEach(function (m) { finder.appendChild(m); });

    var app = document.createElement('div');
    app.id = 'os9-mb-app';
    app.className = 'os9-menubar__group';
    app.style.display = 'none';
    bar.insertBefore(app, right);
    return { bar: bar, finder: finder, app: app };
  }

  function takeMenubar() {
    var g = menubarGroups();
    if (!g) return;
    if (!g.app.dataset.filled) {
      g.app.innerHTML = browserMenusHTML();
      g.app.dataset.filled = '1';
    }
    g.finder.style.display = 'none';
    g.app.style.display = 'flex';
  }

  function releaseMenubar() {
    var g = menubarGroups();
    if (!g) return;
    g.app.style.display = 'none';
    g.app.querySelectorAll('details[open]').forEach(function (d) { d.removeAttribute('open'); });
    g.finder.style.display = 'flex';
  }

  /* Follow the window's focus class rather than patching focusWindow(), so
     this stays self-contained in the browser module. */
  function watchFocus(el) {
    function sync() {
      if (!el.isConnected) { releaseMenubar(); return; }
      if (el.classList.contains('is-active')) takeMenubar(); else releaseMenubar();
    }
    new MutationObserver(sync).observe(el, { attributes: true, attributeFilter: ['class'] });

    /* Closing the window has to hand the menu bar back, and removing an
       element fires no attribute mutation — so the class observer above never
       sees it. Without this the browser's menus stay in the bar after the
       window is gone, with no window for them to act on. */
    var host = el.parentNode;
    if (host) {
      var onRemove = new MutationObserver(function () {
        if (el.isConnected) return;
        releaseMenubar();
        onRemove.disconnect();
      });
      onRemove.observe(host, { childList: true });
    }

    sync();
  }

  /* ── Chrome HTML builder ── */
  function buildBrowserHTML() {
    var registered = Object.keys(window.OS9BrowserPages || {}).filter(function(k) { return k !== HOME_PAGE; });
    var shopLinks  = (window.OS9Config && window.OS9Config.shopLinks) || [
      { handle: 'squiche-art-prints', title: 'Art Prints' },
      { handle: 'squiche-tees',       title: 'Tees'       }
    ];

    /* FIX: page containers are position:absolute children of the content viewport */
    var pageContainers = registered.map(function(key) {
      return '<div id="os9-page-' + key + '" class="os9-scroller" style="display:none;"></div>';
    }).join('');

    var bmBarLinks = shopLinks.map(function(l) {
      return '<a href="#" class="os9-browser__bookmark" data-bm-handle="' + l.handle + '" data-bm-title="' + (l.title || '') + '">' + (l.title || l.handle) + '</a>';
    }).join('') +
      '<a href="#" class="os9-browser__bookmark" data-bm-contact>\u2709\ufe0f Contact</a>' +
      '<a href="#" class="os9-browser__bookmark" data-bm-cart>\uD83D\uDED2 Cart</a>';

    return '<div class="os9-browser" id="os9-browser">' +
      '<div class="os9-browser-chrome">' +

        '<div class="os9-browser__tabs">' +
          _homeTabHTML() +
          '<div class="os9-browser__tab-add" id="os9-tab-add">+</div>' +
        '</div>' +

        '<div class="os9-browser__toolbar">' +
          _toolBtn('os9-back',    'Back',      'Back',      false, false) +
          _toolBtn('os9-forward', 'Forward',   'Forward',   true,  false) +
          '<div class="os9-browser__toolbar-sep"></div>' +
          _toolBtn('os9-stop',    'Stop',      'Stop',      false, true)  +
          _toolBtn('os9-refresh', 'Refresh',   'Refresh',   false, false) +
          _toolBtn('os9-home',    'Home',      'Home',      false, false) +
          '<div class="os9-browser__toolbar-sep"></div>' +
          _toolBtn('os9-search',  'Search',    'Search',    false, false) +
          _toolBtn('os9-bm-btn',  'Favorites', window.innerWidth <= 480 ? 'Faves' : 'Favorites', false, false) +
          '<div class="os9-browser__toolbar-sep os9-browser__toolbar-sep--desktop"></div>' +
          _toolBtn('os9-print',   'Print',     'Print',     false, true) +
          _toolBtn('os9-font',    'Fonts',     'Font',      false, true) +
          /* Mail is visible on mobile too — taps open the contact form fullscreen.
             Same behavior as the Email Me sidebar icon. */
          _toolBtn('os9-mail',    'Mail',      'Mail',      false, false) +
          '<div class="os9-browser__toolbar-sep os9-browser__toolbar-sep--desktop"></div>' +
          _toolBtn('os9-edit',    'Edit',      'Edit',      false, true) +
          /* spacer + logo: spacer fills, logo stays right */
          '<div class="os9-browser__toolbar-spacer"></div>' +
          '<div class="os9-browser__ie-logo"><img src="' + IE_LOGO_SRC + '" alt="IE"></div>' +
        '</div>' +

        '<div class="os9-browser__addressbar">' +
          '<label class="os9-browser__address-label">Address</label>' +
          '<div class="os9-browser__address-input-wrap">' +
            '<input class="os9-browser__address-input" id="os9-address" value="squiche.com" readonly>' +
            '<div class="os9-browser__address-dropdown">\u25be</div>' +
          '</div>' +
          '<div class="os9-browser__links-btn">Links</div>' +
        '</div>' +

        '<div class="os9-browser__bookmarks" id="os9-bookmarks-bar">' + bmBarLinks + '</div>' +

      '</div>' +

      /* Content viewport — dialup overlays attach here, pages are absolute children */
      '<div class="os9-browser-content">' +
        '<div id="os9-page-' + HOME_PAGE + '" class="os9-scroller"></div>' +
        pageContainers +
      '</div>' +

      '<div class="os9-browser__status" id="os9-browser-status">Squiche&#8482;</div>' +
    '</div>';
  }

  /* ── Routing ── */
  /* ═══════════════════════════════════════════════════════════
     § HISTORY

     The browser owns ONE stack for everything: switching tabs, opening
     pages, and a page's own internal navigation. An entry is
     { pageId, url, state } where `state` is opaque — a page hands one over
     from getState() and receives it back through setState().

     Deliberately not two stacks. The alternative was for each page to own
     its own history and answer canGoBack()/goBack() when asked, but then
     home -> mcdonalds -> Careers -> Press needs both stacks to agree on
     ordering across the boundary, and Forward has the same problem in
     reverse. One stack has one ordering and Forward costs nothing.

     Before this there was no history at all: Back read
     `if (_currentPage !== 'home') switchToPage('home')` — a Go Home button
     wearing a Back label — and Forward was a permanently disabled stub.
  ═══════════════════════════════════════════════════════════ */

  /* ═══════════════════════════════════════════════════════════
     PER-TAB HISTORY

     Every tab owns its own stack, the way a real browser works.
     Until 2026-09-28 `_history` / `_histIdx` were module-level
     singletons: two open tabs shared one stack, so pressing Back in
     the second tab navigated the first. A tab is now a record —

         { id, pageId, url, history: [entry...], idx }

     — and Back, Forward and the address bar all read the ACTIVE tab.

     A tab is NOT pinned to the page it was opened for. Clicking a
     SquichaVista result navigates that tab to the lander and pushes
     an entry, so Back returns to the search results, exactly as a
     browser does. That is why the tab key is a serial id and not the
     page key: the page a tab shows changes over its life.

     Page DOM is still one div per registered page, shared across
     tabs. Only one tab is visible at a time, so switching tabs
     re-applies the incoming tab's saved state to the shared div.
     ═══════════════════════════════════════════════════════════ */

  var _tabs      = [];      /* ordered, left to right */
  var _activeId  = null;
  var _tabSeq    = 0;
  var _replaying = false;   /* suppresses pushes while walking the stack */

  function activeTab() {
    for (var i = 0; i < _tabs.length; i++) if (_tabs[i].id === _activeId) return _tabs[i];
    return null;
  }

  function tabById(id) {
    for (var i = 0; i < _tabs.length; i++) if (_tabs[i].id === id) return _tabs[i];
    return null;
  }

  function pageLabel(pageId) {
    var reg = (window.OS9BrowserPages && window.OS9BrowserPages[pageId]) || {};
    var cfg = getPageConfig(pageId);
    return (cfg && cfg.label) || reg.label || pageId;
  }

  function pageUrlFor(pageId) {
    var reg = (window.OS9BrowserPages && window.OS9BrowserPages[pageId]) || {};
    var cfg = getPageConfig(pageId);
    if (pageId === HOME_PAGE) return HOME_URL;
    return reg.url || (cfg && ('www.' + pageId + '.com')) || HOME_URL;
  }

  function makeTab(pageId, url) {
    return {
      id:      ++_tabSeq,
      pageId:  pageId,
      url:     url,
      history: [{ pageId: pageId, url: url, state: null }],
      idx:     0
    };
  }

  /* Ask a page to snapshot itself. Failure is non-fatal: a page with no
     getState() simply has no internal state to remember. */
  function readState(pageId) {
    var reg = window.OS9BrowserPages && window.OS9BrowserPages[pageId];
    if (reg && typeof reg.getState === 'function') {
      try { return reg.getState(); } catch (e) {}
    }
    return null;
  }

  function pushHistory(pageId, url) {
    if (_replaying) return;
    var t = activeTab();
    if (!t) return;
    /* Navigating somewhere new discards anything ahead, as a real browser
       does. */
    if (t.idx < t.history.length - 1) t.history.length = t.idx + 1;
    var top = t.history[t.idx];
    if (top && top.pageId === pageId && top.url === url) return;  /* no-op nav */

    /* The state read here belongs to the entry being CREATED, not the one
       being left. A page changes its section and *then* calls pushState, so
       by the time we run, getState() already reports the destination. An
       earlier version snapshotted into the previous entry instead, which put
       every section one step out of phase: Back showed the right URL beside
       the wrong content. Each entry keeps the state captured when it was
       pushed, so nothing needs re-reading on the way out. */
    t.history.push({ pageId: pageId, url: url, state: readState(pageId) });
    t.idx = t.history.length - 1;
    syncNavButtons();
  }

  function applyEntry(entry) {
    _replaying = true;
    showPage(entry.pageId, entry.url);
    var reg = window.OS9BrowserPages && window.OS9BrowserPages[entry.pageId];
    if (reg && typeof reg.setState === 'function' && entry.state != null) {
      try { reg.setState(entry.state); } catch (e) {}
    }
    _replaying = false;
    syncNavButtons();
  }

  function goBack() {
    var t = activeTab();
    if (!t || t.idx <= 0) return;
    t.idx--;
    applyEntry(t.history[t.idx]);
  }

  function goForward() {
    var t = activeTab();
    if (!t || t.idx >= t.history.length - 1) return;
    t.idx++;
    applyEntry(t.history[t.idx]);
  }

  /* Back and Forward grey out at the ends of the ACTIVE tab's stack, the way
     the real thing does. Forward shipped permanently disabled in the markup;
     this is what makes it a live control. */
  function syncNavButtons() {
    if (!_winEl) return;
    var t = activeTab();
    var b = _winEl.querySelector('#os9-back');
    var f = _winEl.querySelector('#os9-forward');
    var D = 'os9-browser__tool-btn--disabled';
    if (b) b.classList.toggle(D, !t || t.idx <= 0);
    if (f) f.classList.toggle(D, !t || t.idx >= t.history.length - 1);
  }

  /* Called BY A PAGE when it navigates internally — McDondal's Corporate ->
     Careers. The page stays mounted; only the entry and address bar change,
     so the visitor gets Back across a lander's own sections. */
  function pushPageState(url) {
    if (!_currentPage) return;
    var t = activeTab();
    pushHistory(_currentPage, url || (t && t.history[t.idx] && t.history[t.idx].url));
    if (t) t.url = url || t.url;
    var addrEl = _winEl && _winEl.querySelector('#os9-address');
    if (addrEl && url) addrEl.value = url;
  }

  /* showPage — pure view swap. Shows one page div, hides the rest, updates
     the address bar and the active tab's chrome. Records NO history; the
     callers decide that, which is what keeps replaying the stack from
     pushing new entries onto it. */
  function showPage(pageId, pageUrl) {
    if (!_winEl) return;
    _currentPage = pageId;
    var t = activeTab();
    if (t) { t.pageId = pageId; t.url = pageUrl || pageUrlFor(pageId); }

    _winEl.querySelectorAll('[id^="os9-page-"]').forEach(function(p) {
      p.style.display = p.id === 'os9-page-' + pageId ? '' : 'none';
    });
    syncTabChrome();

    var addrEl = _winEl.querySelector('#os9-address');
    if (addrEl) addrEl.value = pageUrl || pageUrlFor(pageId);

    /* Re-measure the page that just became visible.
       Pages are mounted while `display:none`, and a hidden element reports
       zero height — so watchScrollable()'s checks, and any recheck at mount
       time, correctly conclude there is nothing to scroll. Revealing the div
       changes that without firing a mutation or a load event, so the
       observers never wake and `is-scrollable` stays off. The visible symptom
       is greyed arrows on a page with real overflow; measured at 577px on the
       McDondal's lander.

       Next frame, because scrollHeight is not final until layout has run. */
    var shown = _winEl.querySelector('#os9-page-' + pageId);
    if (shown && shown._os9Recheck) {
      requestAnimationFrame(function () { shown._os9Recheck(); });
      setTimeout(function () { shown._os9Recheck(); }, 350);
    }
  }

  /* switchToPage — navigate the ACTIVE tab to a page, recording history.
     This is what a link click does. */
  function switchToPage(pageId, pageUrl) {
    if (!_winEl) return;
    var url = pageUrl || pageUrlFor(pageId);
    pushHistory(pageId, url);
    showPage(pageId, url);
  }

  /* ── Tab management ── */

  /* Redraws every tab's label and active state from its record. A tab's
     title follows whatever page that tab is currently showing, so navigating
     from SquichaVista to a lander renames the tab rather than leaving a
     stale label behind. */
  function syncTabChrome() {
    if (!_winEl) return;
    _tabs.forEach(function (t) {
      var el = _winEl.querySelector('[data-tab-id="' + t.id + '"]');
      if (!el) return;
      el.classList.toggle('os9-browser__tab--active', t.id === _activeId);
      var reg   = (window.OS9BrowserPages && window.OS9BrowserPages[t.pageId]) || {};
      var title = el.querySelector('.os9-browser__tab-title');
      var icon  = el.querySelector('.os9-browser__tab-icon');
      if (title) title.textContent = pageLabel(t.pageId);
      if (icon)  icon.textContent  = reg.icon || '🌐';
      /* Only the last remaining tab loses its close button, so the window is
         never left with no way back to a page. */
      var close = el.querySelector('.os9-browser__tab-close');
      if (close) close.style.display = _tabs.length > 1 ? '' : 'none';
    });
  }

  function buildTabEl(t) {
    var reg = (window.OS9BrowserPages && window.OS9BrowserPages[t.pageId]) || {};

    var tab = document.createElement('div');
    tab.className = 'os9-browser__tab';
    tab.dataset.tabId = t.id;

    var iconSpan = document.createElement('span');
    iconSpan.className = 'os9-browser__tab-icon';
    iconSpan.textContent = reg.icon || '🌐';

    var titleSpan = document.createElement('span');
    titleSpan.className = 'os9-browser__tab-title';
    titleSpan.textContent = pageLabel(t.pageId);

    var closeBtn = document.createElement('span');
    closeBtn.className = 'os9-browser__tab-close';
    closeBtn.title = 'Close tab';
    closeBtn.textContent = '\xd7';
    closeBtn.addEventListener('click', function(e) { e.stopPropagation(); closeTab(t.id); });

    tab.appendChild(iconSpan);
    tab.appendChild(titleSpan);
    tab.appendChild(closeBtn);
    tab.addEventListener('click', function() { selectTab(t.id); });
    return tab;
  }

  /* Home tab markup, built at chrome-build time before any tab record
     exists. wireAll() adopts it as tab #1. */
  function _homeTabHTML() {
    var reg = (window.OS9BrowserPages && window.OS9BrowserPages[HOME_PAGE]) || {};
    var icon = reg.icon || '🌐';
    return '<div class="os9-browser__tab os9-browser__tab--active" data-tab-id="1">' +
             '<span class="os9-browser__tab-icon">' + icon + '</span>' +
             '<span class="os9-browser__tab-title">' + pageLabel(HOME_PAGE) + '</span>' +
             '<span class="os9-browser__tab-close" title="Close tab" style="display:none;">×</span>' +
           '</div>';
  }

  /* selectTab — bring a tab forward. Restores the page IT was last on and
     the state it had, so each tab resumes exactly where it was left. */
  function selectTab(tabId) {
    var t = tabById(tabId);
    if (!t || !_winEl) return;
    _activeId = t.id;
    var entry = t.history[t.idx] || { pageId: t.pageId, url: t.url, state: null };
    _replaying = true;
    showPage(entry.pageId, entry.url);
    var reg = window.OS9BrowserPages && window.OS9BrowserPages[entry.pageId];
    if (reg && typeof reg.setState === 'function' && entry.state != null) {
      try { reg.setState(entry.state); } catch (e) {}
    }
    _replaying = false;
    syncNavButtons();
  }

  /* ═══════════════════════════════════════════════════════════
     tabLimit() — how many tabs may be open at once.

     One per active brand: every registered page is somewhere a tab
     could be pointed, so the ceiling rises on its own as landers are
     registered and never needs editing here. Floor of 2 so the
     browser always opens at least a second tab even mid-build, when
     only SquichaVista has loaded.

     Was a hardcoded 4, which went stale the moment a lander shipped.
     ═══════════════════════════════════════════════════════════ */
  function tabLimit() {
    var n = Object.keys(window.OS9BrowserPages || {}).length;
    return Math.max(2, n);
  }

  /* newTab — a fresh tab on the home page, the way a real browser's New Tab
     lands on the search page. Pass a pageId to open straight to a lander. */
  function newTab(pageId) {
    if (!_winEl) return;
    var target = pageId || HOME_PAGE;
    if (!(window.OS9BrowserPages && window.OS9BrowserPages[target])) return;

    var limit = tabLimit();
    if (_tabs.length >= limit) {
      if (window.OS9Windows && window.OS9Windows.showDialog) {
        window.OS9Windows.showDialog({
          title: 'Internet Browser',
          message: 'Tab limit reached (' + limit + ' tabs maximum). Close a tab to open another.',
          icon: '&#9888;', buttons: 'OK'
        });
      }
      return;
    }

    var url = pageUrlFor(target);
    var t   = makeTab(target, url);
    _tabs.push(t);
    var addBtn = _winEl.querySelector('#os9-tab-add');
    if (addBtn && addBtn.parentNode) addBtn.parentNode.insertBefore(buildTabEl(t), addBtn);
    _activeId = t.id;
    showPage(target, url);
    syncNavButtons();
    showDialup(url);
  }

  /* openTab(pageId) — what a SquichaVista result calls.
     Navigates the CURRENT tab, because that is what clicking a search result
     does in a real browser and it makes Back return to the results. Before
     2026-09-28 this spawned a tab per result, which hit the tab ceiling after
     a few clicks and left Back pointing at whatever the new tab started on. */
  function openTab(pageId) {
    if (!_winEl) return;
    if (!(window.OS9BrowserPages && window.OS9BrowserPages[pageId])) return;
    var url = pageUrlFor(pageId);
    switchToPage(pageId, url);
    showDialup(url);
  }

  function closeTab(tabId) {
    if (!_winEl) return;
    var t = tabById(tabId);
    if (!t) return;

    /* Closing the last tab closes the window, as a browser does. */
    if (_tabs.length <= 1) {
      var closeBtn = _winEl.querySelector('[data-close]');
      if (closeBtn) closeBtn.click();
      return;
    }

    var wasActive = (t.id === _activeId);
    var idx = _tabs.indexOf(t);
    _tabs.splice(idx, 1);
    var el = _winEl.querySelector('[data-tab-id="' + t.id + '"]');
    if (el && el.parentNode) el.parentNode.removeChild(el);

    /* Focus the neighbour, preferring the one to the left — the same choice
       the real thing makes. */
    if (wasActive) selectTab(_tabs[Math.max(0, idx - 1)].id);
    else syncTabChrome();
  }

  /* ── Wire interactions ── */
  function wireAll() {
    /* The home tab ships in the chrome HTML, so it has no listeners yet.
       Adopt it as tab #1 and wire it the same way buildTabEl() wires the
       rest — one code path for every tab. */
    var homeTabEl = _winEl.querySelector('[data-tab-id="1"]');
    if (homeTabEl) {
      homeTabEl.addEventListener('click', function(e) {
        if (e.target.closest('.os9-browser__tab-close')) return;
        selectTab(1);
      });
      var homeClose = homeTabEl.querySelector('.os9-browser__tab-close');
      if (homeClose) homeClose.addEventListener('click', function(e) {
        e.stopPropagation();
        closeTab(1);
      });
    }

    /* "+" opens a NEW tab on the home page — a fresh SquichaVista search,
       the way a period browser's new tab landed on its search engine. */
    var addBtn = _winEl.querySelector('#os9-tab-add');
    if (addBtn) addBtn.addEventListener('click', function() { newTab(); });

    /* ── File menu actions ──────────────────────────────────────────────
       Delegated handler on the window so we don't have to query each
       <a> separately. Aesthetic items (New Window / Open Location /
       Save Page As) use the .is-disabled class which CSS sets to
       pointer-events:none, so they never reach this handler.
       ──────────────────────────────────────────────────────────────── */
    /* On document, not the window: the menus render into the desktop menu bar
       when the browser is frontmost, so a listener bound to _winEl would never
       see them. */
    document.addEventListener('click', function(e) {
      var item = e.target.closest('[data-file-action]');
      if (!item) return;
      e.preventDefault();
      /* Close the dropdown after selection */
      var details = item.closest('details');
      if (details) details.removeAttribute('open');

      var action = item.dataset.fileAction;
      if (action === 'new-tab') {
        newTab();
      } else if (action === 'close-window') {
        var winCloseBtn = _winEl.querySelector('[data-close]');
        if (winCloseBtn) winCloseBtn.click();
      } else if (action === 'close-tab') {
        if (_activeId != null) closeTab(_activeId);
      } else if (action === 'print') {
        window.print();
      } else if (action === 'share') {
        var addrEl = _winEl.querySelector('#os9-address');
        var shareUrl = 'https://' + (addrEl ? addrEl.value : 'squiche.com').replace(/^https?:\/\//, '');
        var shareData = { title: 'Squiche Art', url: shareUrl };
        if (navigator.share) {
          navigator.share(shareData).catch(function() { /* user cancelled — no-op */ });
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(shareUrl).then(function() {
            if (window.OS9Windows && window.OS9Windows.showDialog) {
              window.OS9Windows.showDialog({
                title: 'Share', message: 'URL copied to clipboard.\n\n' + shareUrl,
                icon: '&#9432;', buttons: 'OK'
              });
            }
          });
        }
      }
    });

    /* Close any open menu dropdown when clicking outside the menubar */
    document.addEventListener('click', function(e) {
      if (!_winEl) return;
      if (e.target.closest('.os9-browser__menu-section')) return;
      document.querySelectorAll('.os9-browser__menu-section[open]').forEach(function(d) {
        d.removeAttribute('open');
      });
    });

    /* Bookmarks bar */
    _winEl.querySelectorAll('[data-bm-handle]').forEach(function(a) {
      a.addEventListener('click', function(e) {
        e.preventDefault();
        if (window.OS9Windows) window.OS9Windows.openCollection({ handle: a.dataset.bmHandle, title: a.dataset.bmTitle });
      });
    });
    var bmContact = _winEl.querySelector('[data-bm-contact]');
    if (bmContact) bmContact.addEventListener('click', function(e) {
      e.preventDefault();
      if (window.OS9Windows) window.OS9Windows.openPage({ url: '/pages/contact', title: 'Contact' });
    });
    var bmCart = _winEl.querySelector('[data-bm-cart]');
    if (bmCart) bmCart.addEventListener('click', function(e) {
      e.preventDefault();
      if (window.OS9Windows) window.OS9Windows.openCart({});
    });

    /* Favorites dropdown — FIX: flip to left-aligned when near right edge */
    var bmBtn = _winEl.querySelector('#os9-bm-btn');
    if (bmBtn) {
      var existing = document.getElementById('os9-bm-dropdown');
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

      var bmDropdown = document.createElement('div');
      bmDropdown.id = 'os9-bm-dropdown';
      bmDropdown.style.display = 'none';

      function addDropItem(text, onClick) {
        var a = document.createElement('a');
        a.href = '#';
        a.textContent = text;
        a.addEventListener('click', function(e) {
          e.preventDefault();
          bmDropdown.style.display = 'none';
          onClick();
        });
        bmDropdown.appendChild(a);
      }

      addDropItem((((window.OS9BrowserPages || {})[HOME_PAGE] || {}).icon || '\uD83C\uDF10') + ' ' + pageLabel(HOME_PAGE),
                  function() { switchToPage(HOME_PAGE, HOME_URL); });
      bmDropdown.appendChild(document.createElement('hr'));
      Object.keys(window.OS9BrowserPages || {}).forEach(function(key) {
        if (key === HOME_PAGE) return;
        var reg = window.OS9BrowserPages[key];
        addDropItem((reg.icon || '\uD83C\uDF10') + ' ' + (reg.label || key), function() { openTab(key); });
      });

      /* Off-site bookmarks last, behind their own rule, so the break between
         "pages inside this browser" and "the actual internet" is visible
         before you click rather than after. */
      if (EXTERNAL_FAVORITES.length) {
        bmDropdown.appendChild(document.createElement('hr'));
        EXTERNAL_FAVORITES.forEach(function(fav) {
          addDropItem(fav.icon + ' ' + fav.label + ' \u2197', function() {
            /* noopener matters: without it the opened page gets a handle on
               this window via window.opener and can navigate it. */
            window.open(fav.url, '_blank', 'noopener,noreferrer');
          });
        });
      }

      document.body.appendChild(bmDropdown);

      bmBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        if (bmDropdown.style.display !== 'none') { bmDropdown.style.display = 'none'; return; }

        /* FIX: position dropdown — flip left if it would overflow right edge */
        bmDropdown.style.display = 'block';
        var rect     = bmBtn.getBoundingClientRect();
        var ddW      = bmDropdown.offsetWidth || 180;
        var vw       = window.innerWidth;
        var leftPos  = rect.left;

        if (leftPos + ddW > vw - 8) leftPos = Math.max(4, vw - ddW - 8);
        bmDropdown.style.left = leftPos + 'px';
        bmDropdown.style.top  = rect.bottom + 'px';
      });

      document.addEventListener('click', function(e) {
        if (!bmBtn.contains(e.target) && !bmDropdown.contains(e.target)) {
          bmDropdown.style.display = 'none';
        }
      });

      var winCloseBtn = _winEl.querySelector('[data-close]');
      if (winCloseBtn) {
        winCloseBtn.addEventListener('click', function() {
          if (bmDropdown.parentNode) bmDropdown.parentNode.removeChild(bmDropdown);
        });
      }
    }

    /* Address bar */
    var addrEl = _winEl.querySelector('#os9-address');
    if (addrEl) {
      addrEl.addEventListener('focus', function() { addrEl.removeAttribute('readonly'); addrEl.select(); });
      addrEl.addEventListener('blur',  function() { addrEl.setAttribute('readonly', 'readonly'); });
      addrEl.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') {
          var q = addrEl.value.trim();
          addrEl.blur();
          if (q) {
            openTab('altavista');
            var avReg = window.OS9BrowserPages && window.OS9BrowserPages['altavista'];
            if (avReg && avReg.search) setTimeout(function() { avReg.search(q); }, 150);
          }
        }
      });
    }

    /* Print button — prints the page inside the browser, not the whole site.

       A true screenshot would mean rasterising to a canvas, and the artwork is
       served from the Shopify CDN without CORS headers, so the canvas would be
       tainted and unreadable. Cloning the page's markup into a print-only
       container gets the same result by a route that actually works, and it
       prints as selectable text rather than a bitmap. */
    var printBtn = _winEl.querySelector('#os9-print');
    if (printBtn) printBtn.addEventListener('click', function() {
      var pageEl = _winEl && _winEl.querySelector('#os9-page-' + _currentPage);
      if (!pageEl) { window.print(); return; }
      var holder = document.getElementById('os9-print-root');
      if (holder) holder.remove();
      holder = document.createElement('div');
      holder.id = 'os9-print-root';
      holder.innerHTML = pageEl.innerHTML;
      document.body.appendChild(holder);
      document.documentElement.classList.add('os9-printing');
      function cleanup() {
        var h = document.getElementById('os9-print-root');
        if (h) h.remove();
        document.documentElement.classList.remove('os9-printing');
        window.removeEventListener('afterprint', cleanup);
      }
      window.addEventListener('afterprint', cleanup);
      window.print();
      /* Safari does not reliably fire afterprint. */
      setTimeout(cleanup, 5000);
    });

    /* Search button */
    var searchBtn = _winEl.querySelector('#os9-search');
    if (searchBtn) searchBtn.addEventListener('click', function() { openTab(HOME_PAGE); });

    /* Mail button — opens the contact form. Window manager handles the
       mobile fullscreen treatment via its existing openPage path. */
    var mailBtn = _winEl.querySelector('#os9-mail');
    if (mailBtn) mailBtn.addEventListener('click', function() {
      if (window.OS9Windows) window.OS9Windows.openPage({ url: '/pages/contact', title: 'Email Me' });
    });

    /* Home button — SquichaVista, the portal, rather than the Geocities page.
       Same destination as Search: this browser's idea of "home" is the search
       portal you start from, which is what a 1999 browser's home button
       actually did. */
    var homeBtn = _winEl.querySelector('#os9-home');
    if (homeBtn) homeBtn.addEventListener('click', function() { openTab(HOME_PAGE); });

    /* Back / Forward — walk the history stack.
       No dial-up on either: a real browser serves history from cache, and
       re-playing the modem on every Back would make the illusion tedious
       rather than immersive. */
    var backBtn = _winEl.querySelector('#os9-back');
    if (backBtn) backBtn.addEventListener('click', function() { goBack(); });

    var fwdBtn = _winEl.querySelector('#os9-forward');
    if (fwdBtn) fwdBtn.addEventListener('click', function() { goForward(); });

    syncNavButtons();

    /* Refresh button — dialup then delegate to page module */
    var refreshBtn = _winEl.querySelector('#os9-refresh');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', function() {
        refreshBtn.classList.add('os9-browser__tool-btn--active');
        var addrVal = addrEl ? addrEl.value : (_currentPage + '.com');
        showDialup(addrVal).then(function() {
          var mod = window.OS9BrowserPages && window.OS9BrowserPages[_currentPage];
          if (mod && mod.reload) { try { mod.reload(); } catch(ex) {} }
        });
        setTimeout(function() { refreshBtn.classList.remove('os9-browser__tool-btn--active'); }, 300);
      });
    }
  }

  /* ── Open window ── */
  function openBrowserWindow() {
    var m = window.OS9Windows;
    if (!m) { setTimeout(openBrowserWindow, 100); return; }
    if (m.bringOrOpen('browser')) return;

    injectCSS();

    var desktop = m.getLayer();
    var b  = m.getDesktopBounds();
    var dw = b.width;
    var dh = b.height;
    /* Fixed desktop size rather than one derived from the viewport. Height
       used to be `dh - 16`, i.e. nearly the full desktop, which paired with
       the 660px width cap to give a tall narrow window that read as a phone —
       and a different shape on every monitor. These are clamped so a small
       desktop still gets a window that fits, and mobile is unaffected because
       makeFullscreenOnMobile() takes over below 750px. */
    var DEFAULT_W = 900, DEFAULT_H = 640;
    /* Squiche Explorer's own icon, in the title bar where OS 9 puts the
       window's document icon. Deliberately hardcoded — this is the browser's
       identity, not a theme-editor choice. */
    var TITLE_ICON = 'https://cdn.shopify.com/s/files/1/0777/2844/8754/files/SQUICHEEXPLORER.png?v=1779036030';
    var w  = Math.min(DEFAULT_W, Math.max(300, dw - 20));
    var h  = Math.min(DEFAULT_H, Math.max(360, dh - 20));
    var x  = Math.max(4, Math.floor((dw - w) / 2));
    var y  = b.top + Math.max(0, Math.floor((dh - h) / 2));
    var id = 'os9-browser-win';

    var el = m._buildWindow({
      id: id, title: 'Internet Browser', width: w, height: h, x: x, y: y,
      titleIcon: TITLE_ICON,
    });
    desktop.appendChild(el);
    m._initWindow(el, id, {});
    m._registerTypeKey('browser', id);
    watchFocus(el);

    /* Mobile fullscreen via the shared OS9Windows helper.
       Previously this was duplicated inline — extracted in 2026-06-15
       so the contact window (openPage) and any future fullscreen-on-mobile
       windows share the same geometry behavior. */
    if (m.makeFullscreenOnMobile) m.makeFullscreenOnMobile(el);

    _winEl       = el;
    _currentPage = HOME_PAGE;
    _tabs        = [];
    _activeId    = null;
    _tabSeq      = 0;

    el.querySelector('[data-body]').innerHTML = buildBrowserHTML();

    /* AFTER the markup exists — the page divs are created by buildBrowserHTML,
       so watching them any earlier finds nothing. Gives each the same per-axis
       scrollable classes .os9-win__scroll gets, so their arrows grey out on an
       axis with nothing to scroll. Nothing here scrolls sideways, so the
       horizontal pair correctly sits in its inactive state. */
    if (m.watchScrollable) {
      el.querySelectorAll('[id^="os9-page-"]').forEach(function (pageEl) {
        m.watchScrollable(pageEl);
      });
    }

    /* Mount all page modules */
    Object.keys(window.OS9BrowserPages || {}).forEach(function(key) {
      var mod    = window.OS9BrowserPages[key];
      var pageEl = el.querySelector('#os9-page-' + key);
      var cfg    = getPageConfig(key);
      if (mod && pageEl) {
        mod.mount(pageEl, cfg);
        /* Re-measure after the page fills itself.
           watchScrollable() ran on these divs a moment ago, while they were
           still empty, so its deferred checks all resolved to "nothing to
           scroll". A page that then builds a tall document has no further
           childList mutation and may load no images, so the observers never
           fire again and the div keeps `is-scrollable` off — leaving greyed
           arrows on a page with 577px of overflow. Measured on the McDondal's
           lander, which mounts ~143KB of DOM in one pass.

           A frame later, because scrollHeight is not final until layout. */
        if (pageEl._os9Recheck) {
          requestAnimationFrame(function () { pageEl._os9Recheck(); });
          setTimeout(function () { pageEl._os9Recheck(); }, 400);
        }
      }
    });

    /* Disable dragging when maximized.
       We intercept mousedown/touchstart on the drag handle at capture phase
       and stop propagation so the window manager's drag init never fires.
       Clicks on buttons inside the title bar are explicitly allowed through. */
    function isDragMaximized() {
      var dRect = desktop.getBoundingClientRect();
      var wRect = el.getBoundingClientRect();
      return (wRect.width  >= dRect.width  - 4) &&
             (wRect.height >= dRect.height - 4);
    }

    function blockDragIfMaximized(e) {
      if (e.target.closest('button')) return; /* always let buttons fire */
      if (isDragMaximized()) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    }

    /* 2026-05-20: drag reverted to titlebar [data-drag] — bottom bar is resize-only */
    var dragHandle = el.querySelector('[data-drag]');
    if (dragHandle) {
      dragHandle.addEventListener('mousedown',  blockDragIfMaximized, true);
      dragHandle.addEventListener('touchstart', blockDragIfMaximized, { capture: true, passive: false });
    }

    /* Seed the history with the page already on screen.
       #os9-page-<HOME_PAGE> renders visible at build time, so switchToPage() is
       never called for it and it would otherwise be missing from the stack —
       leaving Back dead after the first navigation, because the entry you
       started from was never recorded. */
    /* Adopt the home tab that buildBrowserHTML() rendered as tab #1, seeded
       with the page already on screen. #os9-page-<HOME_PAGE> renders visible
       at build time, so switchToPage() is never called for it and it would
       otherwise be missing from the stack — leaving Back dead after the first
       navigation, because the entry you started from was never recorded. */
    _tabSeq     = 0;                 /* so makeTab() issues id 1, matching the markup */
    var homeTab = makeTab(HOME_PAGE, HOME_URL);
    _tabs       = [homeTab];
    _activeId   = 1;
    syncNavButtons();

    /* Initial dial-up on home page open */
    showDialup(HOME_URL);

    setTimeout(wireAll, 50);
  }

  window.OS9Browser = {
    open:     openBrowserWindow,
    navigate: function(url) { window.open(url, '_blank'); },

    /* ── For pages with internal navigation ──────────────────────────
       A page that moves between its own sections calls pushState(url)
       so Back and Forward walk those sections too. To have the view
       restored rather than reset, also expose getState()/setState(s)
       on your OS9BrowserPages registration — the browser stores `s`
       opaquely and hands it back on the way through:

         window.OS9BrowserPages['mcdonalds'] = {
           …,
           getState: function () { return currentSectionId; },
           setState: function (s) { showSection(s); },
         };

       Both are optional. Without them a page still gets history at the
       page level; it just reopens at its default view. */
    pushState: pushPageState,
    canGoBack:    function () { var t = activeTab(); return !!t && t.idx > 0; },
    canGoForward: function () { var t = activeTab(); return !!t && t.idx < t.history.length - 1; },
    back:    goBack,
    forward: goForward,
    /* Opens a registered page in its own tab, dial-up and all. Exported so a
       page can hand off to another page — a SquichaVista result set to
       "Custom" opens its lander this way instead of showing the pop-up. */
    openTab:  function(pageId) { openTab(pageId); },

    /* Opens a brand-new tab, on the home page unless a pageId is given.
       This is what File -> New Tab and the "+" button use. */
    newTab:   function(pageId) { newTab(pageId); },

    /* Which page is home, for anything that needs to ask rather than assume. */
    homePage: function () { return HOME_PAGE; },

    /* Current ceiling on simultaneous tabs — one per registered page. */
    tabLimit: tabLimit
  };
})();